import { createSlice, current, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { rootReducer } from '@verantos-dev/app-state-base';
import {
  type ActiveCohortBuilderItemLegacy,
  type ActiveIndexDateItemLegacy,
  type CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangeStatusLegacy,
  CohortBuilderModalLegacy,
  type CohortDslLegacy,
  DerivedEventInputFieldTypeLegacy,
  type DerivedEventLegacy,
  DerivedEventTypeLegacy,
  type DSLValueLegacy,
  type FilterLegacy,
  FilterTypeLegacy,
  type IndexDateRangeLegacy,
  IndexDateTypeLegacy,
  type IndexDateVariableLegacy,
  type SimpleEventLegacy,
  SimpleEventTypeLegacy,
  type StudyPeriodRestrictionsLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  type SubjectVariableLegacy,
  SubjectVariableTypeLegacy,
} from '@verantos-dev/types-cohorts';
import { type ConceptGroup } from '@verantos-dev/types-concepts';
import {
  getFullDslQueryString,
  transformDerivedEventsToDsl,
  transformIndexDateToDsl,
  transformSimpleEventToDsl,
  transformSubjectVariablesToDsl,
  transformSubjectVariableToDsl,
} from '@verantos-dev/util-cohort-definition-ui-to-dsl';

import { COHORT_BUILDER_SLICE_KEY } from './constants/constants';
import { recursiveCheckNextActiveCohortBuilderItemState } from './runtime/getNextActiveCohortBuilderItemState';
import { CohortBuilderSlice, CohortBuilderWorkflow } from './types';
import { addChangeStatusToCohortBuilderItems } from './utils/addChangeStatusToCohortBuilderItems';
import { checkStudyPeriodRestrictionsStatus } from './utils/checkStudyPeriodRestrictionsStatus';
import { computeDiffForCohortBuilderItem } from './utils/computeDiffForCohortBuilderItem';
import { getFullCohortDslForDeleteDerivedEvent } from './utils/dsl-generators/getFullCohortDslForDerivedEvent';
import { getFullCohortDslForDeleteSimpleEvent } from './utils/dsl-generators/getFullCohortDslForSimpleEvent';
import { getFullCohortDslForDeleteSubjectsVariables } from './utils/dsl-generators/getFullCohortDslForSubjectsVariables';
import { getNextCohortDslForStudyPeriodRestriction } from './utils/dsl-generators/getNextCohortDslForStudyPeriodRestriction';
import { getCohortBuilderItemForEditModalById } from './utils/getCohortBuilderItemForEditModalById';
import { getCohortBuilderItemNameAndIdMaps } from './utils/getCohortBuilderItemNameAndIdMaps';
import { getInUseCohortBuilderItemsMap } from './utils/getInUseCohortBuilderItemsMap';
import { getNewCohortBuilderEvent } from './utils/getNewCohortBuilderEvent';
import { getNextCohortBuilderWorkflowForViewedItem } from './utils/getNextCohortBuilderWorkflowForViewedItem';
import { getNextDerivedEventsForDerivedEventNameChange } from './utils/getNextDerivedEventsForDerivedEventNameChange';
import { getNextDerivedEventsForSimpleEventNameChange } from './utils/getNextDerivedEventsForSimpleEventNameChange';
import { getNextFilterForInputFieldValueUpdate } from './utils/getNextFilterForInputFieldValueUpdate';
import { getNextSubjectVariablesForDerivedEventNameChange } from './utils/getNextSubjectVariablesForDerivedEventNameChange';
import { getNextSubjectVariablesForSimpleEventNameChange } from './utils/getNextSubjectVariablesForSimpleEventNameChange';
import { validateCohortDefinitionForProcessing } from './utils/validateCohortDefinitionForProcessing';
import { validateCohortDefinitionForSave } from './utils/validateCohortDefinitionForSave';

const initialState: CohortBuilderSlice = {
  cohortDefinition: undefined,
  cohortBuilderWorkflow: undefined,
  activeCohortBuilderItem: undefined,
  conceptGroups: undefined,
  // isLoggedInUserCohortOwner: false,
  isCohortUpdatedByBuilder: false,
};

export const cohortBuilderSlice = createSlice({
  name: COHORT_BUILDER_SLICE_KEY,
  initialState,
  reducers: {
    /**
     * Reset entire cohort builder slice
     * Sets the cohort definition, cohort builder workflow, and active cohort builder item to undefined
     * @usage -- use this when user exits the cohort builder page
     */
    resetCohortBuilderSlice() {
      return { ...initialState };
    },

    /**
     * Top level slice reducers
     * These reducers are used to set the cohort definition, cohort builder workflow, and active cohort builder item
     *
     * @usage use these when user enters the cohort builder page and we need to initially set the state
     */

    /** Set cohort definition and cohort builder workflow state when entering the page */
    setInitialCohortBuilderSliceState(
      state: CohortBuilderSlice,
      action: PayloadAction<CohortBuilderDefinitionLegacy | undefined>
    ) {
      return {
        ...state,
        cohortDefinition: action.payload
          ? {
              ...action.payload,
              fullCohortDsl: getFullDslQueryString(action.payload.cohortDsl),
            }
          : undefined,
      };
    },

    /** Update the entire cohort definition */
    updateCohortBuilderSliceState(
      state: CohortBuilderSlice,
      action: PayloadAction<CohortBuilderDefinitionLegacy>
    ) {
      // calculate the CohortBuilderItemChangeStatus of each index date, subject variable, simple event, and derived event

      const { cohortDefinitionWithStatuses: nextActiveCohortBuilderState, updatedCohortItems } =
        addChangeStatusToCohortBuilderItems(state.cohortDefinition, action.payload);
      const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;

      return {
        ...state,
        cohortDefinition: action.payload
          ? {
              ...nextActiveCohortBuilderState,
              fullCohortDsl: getFullDslQueryString(action.payload.cohortDsl),
            }
          : undefined,
        cohortBuilderWorkflow: {
          ...state.cohortBuilderWorkflow,
          // actionModal: currentCohortBuilderWorkflow?.actionModal ?? undefined,
          cohortBuilderItemIdsToNamesMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemIdsToNamesMap ?? {},
          cohortBuilderItemNamesToIdsMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemNamesToIdsMap ?? {},
          inUseCohortBuilderItemsMap:
            currentCohortBuilderWorkflow?.inUseCohortBuilderItemsMap ?? {},
          hasUnsavedChanged: validateCohortDefinitionForSave(nextActiveCohortBuilderState),
          isProcessEnabled: validateCohortDefinitionForProcessing(nextActiveCohortBuilderState),
          modal: currentCohortBuilderWorkflow?.modal ?? undefined,
          // activeStep: currentCohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,

          // filter out any items that have been updated
          viewedCohortBuilderItems: Object.keys(
            currentCohortBuilderWorkflow?.viewedCohortBuilderItems ?? {}
          ).reduce((nextViewedCohortBuilderItems: Record<string, boolean>, id) => {
            if (!updatedCohortItems.includes(id)) {
              nextViewedCohortBuilderItems[id] =
                currentCohortBuilderWorkflow?.viewedCohortBuilderItems?.[id] ?? false;
            }
            return nextViewedCohortBuilderItems;
          }, {}),
        },
      };
    },

    /**
     * Concept Groups Reducer
     */
    setConceptGroups(state: CohortBuilderSlice, action: PayloadAction<ConceptGroup[] | undefined>) {
      return {
        ...state,
        conceptGroups: action.payload,
      };
    },

    /**
     * Cohort Builder Definition Reducers
     * These reducers are used to set the cohort definition state
     *
     * @usage use these when user saves a change to a cohort builder
     *         item by clicking the save button in the edit modal
     */

    /** Update the cohort definition */
    setIndexDate(state: CohortBuilderSlice, action: PayloadAction<IndexDateVariableLegacy>) {
      // Insert the new index date dsl string into the cohort dsl
      const currentCohortDsl = state.cohortDefinition?.cohortDsl;
      const newCohortDsl: CohortDslLegacy = {
        inclusionCriteria: currentCohortDsl?.inclusionCriteria ?? [],
        exclusionCriteria: currentCohortDsl?.exclusionCriteria ?? [],
        studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
        subjectVariables: currentCohortDsl?.subjectVariables ?? [],
        indexDate: transformIndexDateToDsl(action.payload),
        simpleEvents: currentCohortDsl?.simpleEvents ?? [],
        derivedEvents: currentCohortDsl?.derivedEvents ?? [],
      };
      const fullCohortDsl = getFullDslQueryString(newCohortDsl);
      const previousIndexDate = state.cohortDefinition?.indexDate;

      let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
      if (previousIndexDate) {
        // if index date previously existed

        if (computeDiffForCohortBuilderItem(previousIndexDate, action.payload)) {
          // if index date previously existed and is different from current update
          if (previousIndexDate.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
            // if index date was new, then it's still new
            _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
          } else {
            // index date is edited
            _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
          }
        } else {
          // if index date previously existed and is the same as current update, then it's unchanged
          _changeStatus =
            previousIndexDate.changeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED;
        }
      } else {
        // if no index date previously existed, then it's new
        _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
      }

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl,
          cohortDsl: newCohortDsl,
          subjectVariables: state.cohortDefinition?.subjectVariables ?? [],
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: {
            ...action.payload,
            changeStatus: _changeStatus,
          },
          simpleEvents: state.cohortDefinition?.simpleEvents ?? [],
          derivedEvents: state.cohortDefinition?.derivedEvents ?? [],
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
      };
    },
    deleteIndexDate(state: CohortBuilderSlice) {
      const currentCohortDsl = state.cohortDefinition?.cohortDsl;
      const newCohortDsl: CohortDslLegacy = {
        inclusionCriteria: currentCohortDsl?.inclusionCriteria ?? [],
        exclusionCriteria: currentCohortDsl?.exclusionCriteria ?? [],
        studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
        subjectVariables: currentCohortDsl?.subjectVariables ?? [],
        indexDate: undefined,
        simpleEvents: currentCohortDsl?.simpleEvents ?? [],
        derivedEvents: currentCohortDsl?.derivedEvents ?? [],
      };

      return {
        ...state,
        cohortDefinition: {
          cohortDsl: newCohortDsl,
          subjectVariables: state.cohortDefinition?.subjectVariables ?? [],
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          fullCohortDsl: getFullDslQueryString(newCohortDsl),
          indexDate: undefined,
          simpleEvents: state.cohortDefinition?.simpleEvents ?? [],
          derivedEvents: state.cohortDefinition?.derivedEvents ?? [],
        },
      };
    },

    /** Subject Variable Reducers */
    setSubjectVariable(state: CohortBuilderSlice, action: PayloadAction<SubjectVariableLegacy>) {
      // Check if the subject variable is already defined in state
      const currentCohortDefinition = state.cohortDefinition;
      const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
      const payloadSubjectVariableIndex = currentSubjectVariables.findIndex(
        (subjectVariable) => subjectVariable.id === action.payload.id
      );

      // If the subject variable is already defined in state, replace it with the new one
      // Otherwise, add the new subject variable to the end of the array
      const nextSubjectVariables =
        payloadSubjectVariableIndex > -1
          ? currentSubjectVariables.map((subjectVariable) => {
              if (subjectVariable.id === action.payload.id) {
                let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
                if (computeDiffForCohortBuilderItem(subjectVariable, action.payload)) {
                  // if subject variable previously existed and is different from current update
                  if (subjectVariable.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                    // if subject variable was new, then it's still new
                    _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
                  } else {
                    // subject variable is edited
                    _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
                  }
                } else {
                  // if subject variable previously existed and is the same as current update, then it's unchanged
                  _changeStatus =
                    subjectVariable.changeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED;
                }

                return {
                  ...action.payload,
                  changeStatus: _changeStatus,
                };
              }
              return subjectVariable;
            })
          : [
              ...currentSubjectVariables,
              { ...action.payload, changeStatus: CohortBuilderItemChangeStatusLegacy.NEW },
            ];

      // If the subject variables exists in state, replace subject variable dsl string
      // otherwise, add the new subject variable dsl string to the end of the array
      const newSubjectVariableDSL = transformSubjectVariableToDsl(action.payload);
      const subjectVariableStrings = currentCohortDefinition?.cohortDsl.subjectVariables ?? [];
      const newCohortDsl: CohortDslLegacy = {
        inclusionCriteria:
          nextSubjectVariables.reduce(
            (nextInclusionCriteria: DSLValueLegacy[], subjectVariable) => {
              if (
                subjectVariable.criteriaSelection ===
                SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION
              ) {
                nextInclusionCriteria.push({ id: subjectVariable.id, value: subjectVariable.name });
              }
              return nextInclusionCriteria;
            },
            []
          ) ?? [],
        exclusionCriteria:
          nextSubjectVariables.reduce(
            (nextInclusionCriteria: DSLValueLegacy[], subjectVariable) => {
              if (
                subjectVariable.criteriaSelection ===
                SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION
              ) {
                nextInclusionCriteria.push({ id: subjectVariable.id, value: subjectVariable.name });
              }
              return nextInclusionCriteria;
            },
            []
          ) ?? [],
        studyPeriodRestrictions: currentCohortDefinition?.cohortDsl.studyPeriodRestrictions ?? [],
        indexDate: currentCohortDefinition?.cohortDsl.indexDate ?? undefined,
        subjectVariables:
          payloadSubjectVariableIndex > -1
            ? subjectVariableStrings.map((subjectVariableString, i) => {
                if (i === payloadSubjectVariableIndex) {
                  return newSubjectVariableDSL;
                }
                return subjectVariableString;
              })
            : [...subjectVariableStrings, newSubjectVariableDSL],
        simpleEvents: currentCohortDefinition?.cohortDsl.simpleEvents ?? [],
        derivedEvents: currentCohortDefinition?.cohortDsl.derivedEvents ?? [],
      };

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: getFullDslQueryString(newCohortDsl),
          cohortDsl: newCohortDsl,
          subjectVariables: nextSubjectVariables,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: state.cohortDefinition?.indexDate,
          simpleEvents: state.cohortDefinition?.simpleEvents ?? [],
          derivedEvents: state.cohortDefinition?.derivedEvents ?? [],
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
      };
    },
    deleteSubjectVariable(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const currentCohortDefinition = state.cohortDefinition;
      const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
      const payloadSubjectVariableIndex = currentSubjectVariables.findIndex(
        (subjectVariable) => subjectVariable.id === action.payload
      );

      const { fullDslQueryString, newCohortDsl } = getFullCohortDslForDeleteSubjectsVariables(
        state.cohortDefinition,
        action.payload
      );
      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: fullDslQueryString,
          cohortDsl: newCohortDsl,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: state.cohortDefinition?.indexDate,
          subjectVariables: [
            ...currentSubjectVariables.slice(0, payloadSubjectVariableIndex),
            ...currentSubjectVariables.slice(payloadSubjectVariableIndex + 1),
          ],
          simpleEvents: state.cohortDefinition?.simpleEvents ?? [],
          derivedEvents: state.cohortDefinition?.derivedEvents ?? [],
        },
      };
    },

    /** Simple Event Reducers */
    setSimpleEvent(state: CohortBuilderSlice, action: PayloadAction<SimpleEventLegacy>) {
      // Check if the simple event is already defined in state
      const currentCohortDefinition = state.cohortDefinition;
      const currentSimpleEvents = currentCohortDefinition?.simpleEvents ?? [];
      const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
      const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
      const payloadSimpleEventIndex = currentSimpleEvents.findIndex(
        (simpleEvent) => simpleEvent.id === action.payload.id
      );

      // check if the name of the simple event has changed, to determine if we need to update the usages of this name
      // in items that reference this simple event.
      const isSimpleEventNameChanged =
        payloadSimpleEventIndex > -1 &&
        currentSimpleEvents[payloadSimpleEventIndex].name !== action.payload.name;

      const nextDerivedEvents = isSimpleEventNameChanged
        ? getNextDerivedEventsForSimpleEventNameChange(
            currentDerivedEvents,
            currentSimpleEvents[payloadSimpleEventIndex].name,
            action.payload.name,
            action.payload.type
          )
        : currentDerivedEvents;
      const nextSubjectVariables = isSimpleEventNameChanged
        ? getNextSubjectVariablesForSimpleEventNameChange(
            currentSubjectVariables,
            currentSimpleEvents[payloadSimpleEventIndex].name,
            action.payload.name,
            action.payload.type
          )
        : currentSubjectVariables;

      // If the simple events exists in state, replace simple event dsl string
      // otherwise, add the new simple event dsl string to the end of the array
      const newSimpleEventDSL = transformSimpleEventToDsl(action.payload);
      const simpleEventStrings = currentCohortDefinition?.cohortDsl.simpleEvents ?? [];
      const newCohortDsl: CohortDslLegacy = {
        inclusionCriteria: currentCohortDefinition?.cohortDsl.inclusionCriteria ?? [],
        exclusionCriteria: currentCohortDefinition?.cohortDsl.exclusionCriteria ?? [],
        studyPeriodRestrictions: currentCohortDefinition?.cohortDsl.studyPeriodRestrictions ?? [],
        subjectVariables: transformSubjectVariablesToDsl(nextSubjectVariables),
        indexDate: currentCohortDefinition?.cohortDsl.indexDate ?? undefined,
        derivedEvents: transformDerivedEventsToDsl(nextDerivedEvents),
        simpleEvents:
          payloadSimpleEventIndex > -1
            ? simpleEventStrings.map((simpleEventString, i) => {
                if (i === payloadSimpleEventIndex) {
                  return newSimpleEventDSL;
                }
                return simpleEventString;
              })
            : [...simpleEventStrings, newSimpleEventDSL],
      };

      // If the simple event is already defined in state, replace it with the new one
      // Otherwise, add the new simple event to the end of the array
      const simpleEvents =
        payloadSimpleEventIndex > -1
          ? currentSimpleEvents.map((simpleEvent) => {
              if (simpleEvent.id === action.payload.id) {
                let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
                if (computeDiffForCohortBuilderItem(simpleEvent, action.payload)) {
                  // if simple event previously existed and is different from current update
                  if (simpleEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                    // if simple event was new, then it's still new
                    _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
                  } else {
                    // simple event is edited
                    _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
                  }
                } else {
                  // if simple event previously existed and is the same as current update, then it's unchanged
                  _changeStatus =
                    simpleEvent.changeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED;
                }

                return {
                  ...action.payload,
                  changeStatus: _changeStatus,
                };
              }
              return simpleEvent;
            })
          : [
              ...currentSimpleEvents,
              { ...action.payload, changeStatus: CohortBuilderItemChangeStatusLegacy.NEW },
            ];

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: getFullDslQueryString(newCohortDsl),
          cohortDsl: newCohortDsl,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: nextSubjectVariables,
          indexDate: state.cohortDefinition?.indexDate,
          simpleEvents,
          derivedEvents: nextDerivedEvents,
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
      };
    },
    deleteSimpleEvent(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const { newCohortDsl, fullDslQueryString } = getFullCohortDslForDeleteSimpleEvent(
        state.cohortDefinition,
        action.payload
      );

      const currentSimpleEvents = state.cohortDefinition?.simpleEvents ?? [];
      const payloadSimpleEventIndex = currentSimpleEvents.findIndex(
        (simpleEvent) => simpleEvent.id === action.payload
      );

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: fullDslQueryString,
          cohortDsl: newCohortDsl,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: state.cohortDefinition?.indexDate,
          subjectVariables: state.cohortDefinition?.subjectVariables ?? [],
          simpleEvents: [
            ...currentSimpleEvents.slice(0, payloadSimpleEventIndex),
            ...currentSimpleEvents.slice(payloadSimpleEventIndex + 1),
          ],
          derivedEvents: state.cohortDefinition?.derivedEvents ?? [],
        },
      };
    },

    /** Derived Event Reducers */
    setDerivedEvent(state: CohortBuilderSlice, action: PayloadAction<DerivedEventLegacy>) {
      // Check if the derived event is already defined in state
      const currentCohortDefinition = state.cohortDefinition;
      const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
      const currentIndexDate = currentCohortDefinition?.indexDate;
      const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
      const payloadDerivedEventIndex = currentDerivedEvents.findIndex(
        (derivedEvents) => derivedEvents.id === action.payload.id
      );

      // check if the name of the simple event has changed, to determine if we need to update the usages of this name
      // in items that reference this simple event.
      const isDerivedEventNameChanged =
        payloadDerivedEventIndex > -1 &&
        currentDerivedEvents[payloadDerivedEventIndex].name !== action.payload.name;

      const nextSubjectVariables = isDerivedEventNameChanged
        ? getNextSubjectVariablesForDerivedEventNameChange(
            currentSubjectVariables,
            currentDerivedEvents[payloadDerivedEventIndex].name,
            action.payload.name,
            action.payload.type
          )
        : currentSubjectVariables;

      let nextIndexDate: IndexDateVariableLegacy | undefined = currentIndexDate;
      if (
        isDerivedEventNameChanged &&
        currentIndexDate &&
        currentIndexDate.type === IndexDateTypeLegacy.EVENT
      ) {
        const currentIndexDateEventName = currentIndexDate.indexDateValue.split('::')[1];
        if (
          currentIndexDateEventName.slice(1, currentIndexDateEventName.length - 1) ===
          currentDerivedEvents[payloadDerivedEventIndex].name
        ) {
          nextIndexDate = {
            ...currentIndexDate,
            indexDateValue: `INSTANCE_EVENT::"${action.payload.name}"`,
          };
        }
      }

      const nameChangedDerivedEvents = isDerivedEventNameChanged
        ? getNextDerivedEventsForDerivedEventNameChange(
            currentDerivedEvents,
            currentDerivedEvents[payloadDerivedEventIndex].name,
            action.payload.name,
            action.payload.type
          )
        : currentDerivedEvents;

      // If the derived event is already defined in state, replace it with the new one
      // Otherwise, add the new derived event to the end of the array
      const nextDerivedEvents =
        payloadDerivedEventIndex > -1
          ? nameChangedDerivedEvents.map((derivedEvent) => {
              if (derivedEvent.id === action.payload.id) {
                let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
                if (computeDiffForCohortBuilderItem(derivedEvent, action.payload)) {
                  // if derived event previously existed and is different from current update
                  if (derivedEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                    // if derived event was new, then it's still new
                    _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
                  } else {
                    // derived event is edited
                    _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
                  }
                } else {
                  // if derived event previously existed and is the same as current update, then it's unchanged
                  _changeStatus =
                    derivedEvent.changeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED;
                }

                return {
                  ...action.payload,
                  changeStatus: _changeStatus,
                };
              }
              return derivedEvent;
            })
          : [
              ...nameChangedDerivedEvents,
              {
                ...action.payload,
                changeStatus: CohortBuilderItemChangeStatusLegacy.NEW,
              },
            ];

      const newCohortDsl: CohortDslLegacy = {
        inclusionCriteria: currentCohortDefinition?.cohortDsl.inclusionCriteria ?? [],
        exclusionCriteria: currentCohortDefinition?.cohortDsl.exclusionCriteria ?? [],
        studyPeriodRestrictions: currentCohortDefinition?.cohortDsl.studyPeriodRestrictions ?? [],
        indexDate: nextIndexDate ? transformIndexDateToDsl(nextIndexDate) : undefined,
        subjectVariables: transformSubjectVariablesToDsl(nextSubjectVariables),
        simpleEvents: currentCohortDefinition?.cohortDsl.simpleEvents ?? [],
        derivedEvents: transformDerivedEventsToDsl(nextDerivedEvents),
      };

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: getFullDslQueryString(newCohortDsl),
          cohortDsl: newCohortDsl,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: nextIndexDate,
          subjectVariables: nextSubjectVariables,
          simpleEvents: state.cohortDefinition?.simpleEvents ?? [],
          derivedEvents: nextDerivedEvents,
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
      };
    },
    deleteDerivedEvent(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const currentDerivedEvents = state.cohortDefinition?.derivedEvents ?? [];
      const payloadDerivedEventIndex = currentDerivedEvents.findIndex(
        (derivedEvent) => derivedEvent.id === action.payload
      );

      const { fullDslQueryString, newCohortDsl } = getFullCohortDslForDeleteDerivedEvent(
        state.cohortDefinition,
        action.payload
      );

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: fullDslQueryString,
          cohortDsl: newCohortDsl,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: state.cohortDefinition?.indexDate,
          subjectVariables: state.cohortDefinition?.subjectVariables ?? [],
          simpleEvents: state.cohortDefinition?.simpleEvents ?? [],
          derivedEvents: [
            ...currentDerivedEvents.slice(0, payloadDerivedEventIndex),
            ...currentDerivedEvents.slice(payloadDerivedEventIndex + 1),
          ],
        },
      };
    },

    /** Cohort Definition Selection Action - updates the cohort definition selection on a subject variable */
    setCriteriaSelection(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        subjectVariableId: string;
        subjectVariableName: string;
        criteriaSelection: SubjectVariableCriteriaSelectionTypeLegacy;
      }>
    ) {
      /** We need to update the DSL to include the inclusion and exclusion criteria subject variable names */
      let inclusionCriteria: DSLValueLegacy[] = [];
      let exclusionCriteria: DSLValueLegacy[] = [];
      const { subjectVariableId, subjectVariableName } = action.payload;

      const currentCohortDefinition = state.cohortDefinition;
      const currentCohortDsl = currentCohortDefinition?.cohortDsl;
      const currentInclusionCriteriaDsl = currentCohortDsl?.inclusionCriteria ?? [];
      const currentExclusionCriteriaDsl = currentCohortDsl?.exclusionCriteria ?? [];

      switch (action.payload.criteriaSelection) {
        // If the criteria selection is off, remove the subject variable from both inclusion and exclusion criteria
        case SubjectVariableCriteriaSelectionTypeLegacy.NOT_APPLIED:
          inclusionCriteria = currentInclusionCriteriaDsl.filter(
            (criteria) => criteria.id !== subjectVariableId
          );
          exclusionCriteria = currentExclusionCriteriaDsl.filter(
            (criteria) => criteria.id !== subjectVariableId
          );
          break;

        // If the criteria selection is inclusion, remove the subject variable from exclusion criteria and add it to inclusion criteria
        case SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION: {
          const existsInInclusionCriteria =
            currentCohortDsl?.inclusionCriteria.some(
              (criteria) => criteria.id === subjectVariableId
            ) ?? false;

          // remove from exclusion criteria
          exclusionCriteria = currentExclusionCriteriaDsl.filter(
            (criteria) => criteria.id !== subjectVariableId
          );

          // add to inclusion criteria
          if (!existsInInclusionCriteria) {
            inclusionCriteria = [
              ...currentInclusionCriteriaDsl,
              {
                id: subjectVariableId,
                value: subjectVariableName,
              },
            ];
          }
          break;
        }

        // If the criteria selection is exclusion, remove the subject variable from inclusion criteria and add it to exclusion criteria
        case SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION: {
          const existsInExclusionCriteria =
            currentCohortDsl?.exclusionCriteria.some(
              (criteria) => criteria.id === subjectVariableId
            ) ?? false;

          // remove from inclusion criteria
          inclusionCriteria = currentInclusionCriteriaDsl.filter(
            (criteria) => criteria.id !== subjectVariableId
          );
          // add to exclusion criteria
          if (!existsInExclusionCriteria) {
            exclusionCriteria = [
              ...currentExclusionCriteriaDsl,
              {
                id: subjectVariableId,
                value: subjectVariableName,
              },
            ];
          }
          break;
        }
      }

      const newCohortDsl: CohortDslLegacy = {
        indexDate: currentCohortDsl?.indexDate,
        subjectVariables: currentCohortDsl?.subjectVariables ?? [],
        studyPeriodRestrictions: currentCohortDsl?.studyPeriodRestrictions ?? [],
        simpleEvents: currentCohortDsl?.simpleEvents ?? [],
        derivedEvents: currentCohortDsl?.derivedEvents ?? [],
        inclusionCriteria,
        exclusionCriteria,
      };

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: getFullDslQueryString(newCohortDsl),
          cohortDsl: newCohortDsl,
          studyPeriodRestrictions: state.cohortDefinition?.studyPeriodRestrictions,
          indexDate: currentCohortDefinition?.indexDate,
          subjectVariables:
            currentCohortDefinition?.subjectVariables?.map((subjectVariable) => {
              if (subjectVariable.id === subjectVariableId) {
                return {
                  ...subjectVariable,
                  criteriaSelection: action.payload.criteriaSelection,
                  changeStatus:
                    subjectVariable.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW
                      ? CohortBuilderItemChangeStatusLegacy.NEW
                      : CohortBuilderItemChangeStatusLegacy.EDITED,
                };
              }
              return subjectVariable;
            }) ?? [],
          simpleEvents: currentCohortDefinition?.simpleEvents ?? [],
          derivedEvents: currentCohortDefinition?.derivedEvents ?? [],
        },
      };
    },

    setStudyPeriodRestrictions(
      state: CohortBuilderSlice,
      action: PayloadAction<StudyPeriodRestrictionsLegacy>
    ) {
      const currentCohortDefinition = state.cohortDefinition;
      const currentCohortDsl = currentCohortDefinition?.cohortDsl;
      const studyPeriodStatus = checkStudyPeriodRestrictionsStatus(
        currentCohortDefinition?.studyPeriodRestrictions,
        action.payload
      );
      const nextCohortDsl = getNextCohortDslForStudyPeriodRestriction(
        action.payload,
        currentCohortDsl
      );

      return {
        ...state,
        cohortDefinition: {
          fullCohortDsl: getFullDslQueryString(nextCohortDsl),
          cohortDsl: nextCohortDsl,
          studyPeriodRestrictions: { ...action.payload, changeStatus: studyPeriodStatus },
          indexDate: currentCohortDefinition?.indexDate,
          subjectVariables: currentCohortDefinition?.subjectVariables ?? [],
          simpleEvents: currentCohortDefinition?.simpleEvents ?? [],
          derivedEvents: currentCohortDefinition?.derivedEvents ?? [],
        },
      };
    },

    /**
     * Cohort Builder Workflow Reducers
     *
     * These reducers are used to:
     * - manage the state of the cohort builder workflow
     * - manage the state of the cohort builder edit modal
     */

    markItemViewed(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;
      if (currentCohortBuilderWorkflow === undefined) {
        throw new Error('setActiveStep: Cohort Builder Workflow is undefined');
      }

      return {
        ...state,
        cohortBuilderWorkflow: {
          ...state.cohortBuilderWorkflow,
          // actionModal: currentCohortBuilderWorkflow?.actionModal ?? undefined,
          cohortBuilderItemIdsToNamesMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemIdsToNamesMap ?? {},
          cohortBuilderItemNamesToIdsMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemNamesToIdsMap ?? {},
          inUseCohortBuilderItemsMap:
            currentCohortBuilderWorkflow?.inUseCohortBuilderItemsMap ?? {},
          hasUnsavedChanged: currentCohortBuilderWorkflow?.hasUnsavedChanged ?? false,
          isProcessEnabled: currentCohortBuilderWorkflow?.isProcessEnabled ?? false,
          modal: currentCohortBuilderWorkflow?.modal ?? undefined,
          // activeStep: currentCohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,
          viewedCohortBuilderItems: {
            ...state.cohortBuilderWorkflow?.viewedCohortBuilderItems,
            [action.payload]: true,
          },
        },
      };
    },

    // Set the active step in the workflow
    // setActiveStep(state: CohortBuilderSlice, action: PayloadAction<CohortBuilderStepLegacy>) {
    //   const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;
    //   if (currentCohortBuilderWorkflow === undefined) {
    //     throw new Error('setActiveStep: Cohort Builder Workflow is undefined');
    //   }

    //   return {
    //     ...state,
    //     cohortBuilderWorkflow: {
    //       ...currentCohortBuilderWorkflow,
    //       hasUnsavedChanged: currentCohortBuilderWorkflow?.hasUnsavedChanged ?? false,
    //       isProcessEnabled: currentCohortBuilderWorkflow?.isProcessEnabled ?? false,
    //       modal: currentCohortBuilderWorkflow?.modal ?? undefined,
    //       // activeStep: action.payload,
    //     },
    //   };
    // },

    // Set the modal to display
    setModal(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        modal: CohortBuilderModalLegacy;
      }>
    ) {
      const currentCohortBuilderWorkflow = current(state.cohortBuilderWorkflow);
      const currentCohortDefinition = current(state.cohortDefinition);
      if (currentCohortBuilderWorkflow === undefined) {
        throw new Error('setModal: Cohort Builder Workflow is undefined');
      }
      if (currentCohortDefinition === undefined) {
        throw new Error('setModal: Cohort Definition is undefined');
      }

      const loadedActiveCohortBuilderItem = getCohortBuilderItemForEditModalById(
        action.payload.id,
        action.payload.modal,
        currentCohortDefinition
      );
      const nextCohortBuilderWorkflow: CohortBuilderWorkflow = {
        ...currentCohortBuilderWorkflow,
        modal: action.payload.modal,
        hasUnsavedChanged: currentCohortBuilderWorkflow?.hasUnsavedChanged ?? false,
        isProcessEnabled: currentCohortBuilderWorkflow?.isProcessEnabled ?? false,
        // activeStep: currentCohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,
      };
      const currentModalType = nextCohortBuilderWorkflow?.modal;
      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        action.payload.modal,
        currentCohortDefinition,
        loadedActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: loadedActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition,
          derivedEventType:
            currentModalType &&
            [
              CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
              CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
            ].includes(currentModalType)
              ? (loadedActiveCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );
      return {
        ...state,
        cohortBuilderWorkflow: nextCohortBuilderWorkflow,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    // clear the modal
    clearModal(state: CohortBuilderSlice) {
      if (state.cohortBuilderWorkflow === undefined) {
        throw new Error('clearModal: cohortBuilderWorkflow is undefined');
      }
      return {
        ...state,
        cohortBuilderWorkflow: {
          ...state.cohortBuilderWorkflow,
          modal: undefined,
        },
        activeCohortBuilderItem: undefined,
      };
    },

    // Set the modal to display
    // setActionModal(
    //   state: CohortBuilderSlice,
    //   action: PayloadAction<{
    //     id: string;
    //     modal: CohortActionModalLegacy;
    //   }>
    // ) {
    //   const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;
    //   const currentCohortDefinition = state.cohortDefinition;
    //   if (currentCohortBuilderWorkflow === undefined) {
    //     throw new Error('setActionModal: Cohort Builder Workflow is undefined');
    //   }
    //   if (currentCohortDefinition === undefined) {
    //     throw new Error('setActionModal: Cohort Definition is undefined');
    //   }

    //   return {
    //     ...state,
    //     cohortBuilderWorkflow: {
    //       ...currentCohortBuilderWorkflow,
    //       // actionModal: action.payload.modal,
    //     },
    //   };
    // },

    // clear the action modal
    // clearActionModal(state: CohortBuilderSlice) {
    //   if (state.cohortBuilderWorkflow === undefined) {
    //     throw new Error('clearActionModal: cohortBuilderWorkflow is undefined');
    //   }
    //   return {
    //     ...state,
    //     cohortBuilderWorkflow: {
    //       ...state.cohortBuilderWorkflow,
    //       actionModal: undefined,
    //     },
    //   };
    // },

    /**
     * Active Cohort Builder Item Reducers
     * These reducers are used to:
     * - manage the state of the active cohort builder item that is displayed in the edit modal
     *
     * @usage use these when the user is in the edit modal
     */

    // Set the active cohort builder item name
    setActiveCohortBuilderItemName(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        name: string;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      // If the name and type exist, then pass through the runtime
      if (activeCohortBuilderItem.name && activeCohortBuilderItem.type) {
        const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
          ...activeCohortBuilderItem,
          name: action.payload.name,
        };

        const currentModalType = state.cohortBuilderWorkflow?.modal;
        const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          state.cohortBuilderWorkflow?.modal,
          state.cohortDefinition,
          currentActiveCohortBuilderItem,
          {
            activeCohortBuilderItem: currentActiveCohortBuilderItem,
            conceptGroups: state.conceptGroups,
            currentCohortDefinition: state.cohortDefinition,
            derivedEventType:
              currentModalType &&
              [
                CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
                CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
              ].includes(currentModalType)
                ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
                : undefined,
          }
        );

        return {
          ...state,
          activeCohortBuilderItem: nextActiveCohortBuilderItem,
        };
      }

      return {
        ...state,
        activeCohortBuilderItem: { ...activeCohortBuilderItem, name: action.payload.name },
      };
    },

    // Set the active cohort builder item subject variable type
    setActiveCohortBuilderItemSubjectVariableType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: SubjectVariableTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const hasTypeSelectionChanged = state.activeCohortBuilderItem?.type !== action.payload.type;

      // only pass through the runtime if the name and type exist
      if (activeCohortBuilderItem.name && hasTypeSelectionChanged) {
        const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
          ...activeCohortBuilderItem,
          type: action.payload.type,
          conceptFilters: [], // reset the concept filters
          numericFilters: [], // reset the numeric filters
          countFilter: undefined, // reset the count filter
        };

        const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          state.cohortBuilderWorkflow?.modal,
          state.cohortDefinition,
          currentActiveCohortBuilderItem,
          {
            activeCohortBuilderItem: currentActiveCohortBuilderItem,
            conceptGroups: state.conceptGroups,
            currentCohortDefinition: state.cohortDefinition,
            derivedEventType: undefined,
          }
        );

        return {
          ...state,
          activeCohortBuilderItem: nextActiveCohortBuilderItem,
        };
      }

      return {
        ...state,
        activeCohortBuilderItem: {
          ...activeCohortBuilderItem,
          type: action.payload.type,
        },
      };
    },

    // Set the active cohort builder item simple event type
    setActiveCohortBuilderItemSimpleEventType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: SimpleEventTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      if (activeCohortBuilderItem.type === action.payload.type) {
        return state;
      }

      return {
        ...state,
        activeCohortBuilderItem: {
          ...activeCohortBuilderItem,
          type: action.payload.type,
          conceptFilters: [],
          numericFilters: [],
          relatedEntityFilters: [],
          uniquenessFilters: [],
        },
      };
    },

    // Set the active cohort builder item derived event type
    setActiveCohortBuilderItemDerivedEventType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: DerivedEventTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const hasTypeSelectionChanged = state.activeCohortBuilderItem?.type !== action.payload.type;

      // if the name is set and the type has changed, reset the events and pass through the runtime
      if (activeCohortBuilderItem.name && hasTypeSelectionChanged) {
        const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
          ...activeCohortBuilderItem,
          type: action.payload.type,
          // reset the events when the type changes, otherwise keep the existing events
          eventFilters: hasTypeSelectionChanged
            ? []
            : state.activeCohortBuilderItem?.eventFilters ?? [],
          // reset the temporal constraints when the type changes, otherwise keep the existing temporal constraints
          temporalConstraints: hasTypeSelectionChanged
            ? []
            : state.activeCohortBuilderItem?.temporalConstraints ?? [],
          uniquenessFilters: hasTypeSelectionChanged
            ? []
            : state.activeCohortBuilderItem?.uniquenessFilters ?? [],
          // inputFieldConfigMap: getNextInputFieldItemsMapForDerivedEvent(events),
        };

        const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          state.cohortBuilderWorkflow?.modal,
          state.cohortDefinition,
          currentActiveCohortBuilderItem,
          {
            activeCohortBuilderItem: currentActiveCohortBuilderItem,
            conceptGroups: state.conceptGroups,
            currentCohortDefinition: state.cohortDefinition,
            derivedEventType: action.payload.type,
          }
        );

        return {
          ...state,
          activeCohortBuilderItem: nextActiveCohortBuilderItem,
        };
      }

      // only update the type if the name is not set
      return {
        ...state,
        activeCohortBuilderItem: {
          ...activeCohortBuilderItem,
          type: action.payload.type,
        },
      };
    },

    // Set the active cohort builder item index date type
    setActiveCohortBuilderItemIndexDateType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: IndexDateTypeLegacy;
      }>
    ) {
      const _activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (_activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      // Do nothing if the type is the same
      if (_activeCohortBuilderItem.type === action.payload.type) {
        return state;
      }

      const activeCohortBuilderItem: ActiveIndexDateItemLegacy = {
        ..._activeCohortBuilderItem,
        type: action.payload.type,
        indexDateValue: undefined,
        indexDateRangeValue: undefined,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        activeCohortBuilderItem,
        {
          activeCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    setActiveCohortBuilderItemIndexDateValue(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: IndexDateTypeLegacy;
        indexDateValue: string;
        indexDateRangeValue?: IndexDateRangeLegacy;
      }>
    ) {
      const _activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (_activeCohortBuilderItem === undefined) {
        throw new Error(
          'setActiveCohortBuilderItemIndexDateValue: activeCohortBuilderItem is undefined'
        );
      }

      if (_activeCohortBuilderItem.id !== action.payload.id) {
        throw new Error(
          `setActiveCohortBuilderItemIndexDateValue: activeCohortBuilderItem id does not match\nActive id: ${_activeCohortBuilderItem.id}\nPayload id: ${action.payload.id}`
        );
      }

      if (_activeCohortBuilderItem.type !== action.payload.type) {
        throw new Error(
          `setActiveCohortBuilderItemIndexDateValue: activeCohortBuilderItem type does not match\nActive type: ${_activeCohortBuilderItem.type}\nPayload type: ${action.payload.type}`
        );
      }

      const activeCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ..._activeCohortBuilderItem,
        indexDateValue: action.payload.indexDateValue,
        indexDateRangeValue: action.payload.indexDateRangeValue,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        activeCohortBuilderItem,
        {
          activeCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    addActiveCohortBuilderEventForDerivedEventType(
      state: CohortBuilderSlice,
      action: PayloadAction<DerivedEventTypeLegacy>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        eventFilters: [
          ...activeCohortBuilderItem.eventFilters,
          getNewCohortBuilderEvent(action.payload),
        ],
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: action.payload,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },
    setActiveCohortBuilderEventInputFieldValue(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        derivedEventFilterId: string; // the id of the derived event being updated
        derivedEventType: DerivedEventTypeLegacy;
        derivedEventFieldType: DerivedEventInputFieldTypeLegacy;
        value: string;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const eventNameChanged =
        action.payload.derivedEventFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME &&
        activeCohortBuilderItem.name !== action.payload.value;
      const derivedEventInputFieldType = action.payload.derivedEventFieldType;

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        eventFilters: activeCohortBuilderItem.eventFilters.map((event) => {
          if (event.id === action.payload.derivedEventFilterId) {
            let nextEventDateAttributeValue = event.eventDateAttribute.value;
            let nextPositionValue =
              action.payload.derivedEventType === DerivedEventTypeLegacy.INSTANCE
                ? {
                    id: event.position?.id ?? '',
                    value:
                      derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_POSITION
                        ? action.payload.value
                        : event.position?.value ?? '',
                  }
                : undefined;
            let nextIndexValue =
              action.payload.derivedEventType === DerivedEventTypeLegacy.INSTANCE
                ? {
                    id: event.index?.id ?? '',
                    value:
                      derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX
                        ? action.payload.value
                        : event.index?.value ?? '',
                  }
                : undefined;
            if (
              derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE
            ) {
              nextEventDateAttributeValue = action.payload.value;
            }
            if (
              derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME &&
              eventNameChanged
            ) {
              nextEventDateAttributeValue = '';
              nextPositionValue = nextPositionValue && { ...nextPositionValue, value: '' };
              nextIndexValue = nextIndexValue && { ...nextIndexValue, value: '' };
            }
            if (nextPositionValue?.value !== 'INDEX') {
              nextIndexValue = undefined;
            }

            return {
              ...event,
              eventName: {
                ...event.eventName,
                value:
                  derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME
                    ? action.payload.value
                    : event.eventName.value,
              },
              eventDateAttribute: {
                ...event.eventDateAttribute,
                value: nextEventDateAttributeValue,
              },
              isSelected:
                action.payload.derivedEventType === DerivedEventTypeLegacy.SEQUENCE
                  ? {
                      id: event.isSelected?.id ?? '',
                      value:
                        derivedEventInputFieldType ===
                          DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED &&
                        action.payload.value === 'true' // only allow switching to true, we should never uncheck but instead select a different event to be true
                          ? action.payload.value
                          : event.isSelected?.value ?? '',
                    }
                  : undefined,
              position: nextPositionValue,
              index: nextIndexValue,
            };
          }
          return {
            ...event,
            isSelected:
              action.payload.derivedEventType === DerivedEventTypeLegacy.SEQUENCE
                ? {
                    id: event.isSelected?.id ?? '',
                    value:
                      derivedEventInputFieldType ===
                        DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED &&
                      action.payload.value === 'true'
                        ? 'false'
                        : event.isSelected?.value ?? '',
                  }
                : undefined,
          };
        }),
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: action.payload.derivedEventType,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },
    deleteActiveCohortBuilderEvent(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        eventFilters: activeCohortBuilderItem.eventFilters.filter(
          (event) => event.id !== action.payload
        ),
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: activeCohortBuilderItem.type as DerivedEventTypeLegacy,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    setActiveCohortBuilderItemAnyOrder(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        anyOrder: action.payload,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: activeCohortBuilderItem.type as DerivedEventTypeLegacy,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /** When a user clicks to add a new filter */
    addActiveCohortBuilderItemFilter(
      state: CohortBuilderSlice,
      action: PayloadAction<FilterTypeLegacy>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const newFilter: FilterLegacy = {
        id: uuidv4(),
        inputFields: [],
      };

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        conceptFilters:
          action.payload === FilterTypeLegacy.CONCEPT
            ? [...activeCohortBuilderItem.conceptFilters, newFilter]
            : activeCohortBuilderItem.conceptFilters,
        numericFilters:
          action.payload === FilterTypeLegacy.NUMERIC
            ? [...activeCohortBuilderItem.numericFilters, newFilter]
            : activeCohortBuilderItem.numericFilters,
        relatedEntityFilters:
          action.payload === FilterTypeLegacy.RELATED_ENTITY
            ? [...(activeCohortBuilderItem.relatedEntityFilters ?? []), newFilter]
            : activeCohortBuilderItem.relatedEntityFilters,
        uniquenessFilters:
          action.payload === FilterTypeLegacy.UNIQUENESS
            ? [...(activeCohortBuilderItem.uniquenessFilters ?? []), newFilter]
            : activeCohortBuilderItem.uniquenessFilters,
        temporalConstraints:
          action.payload === FilterTypeLegacy.TEMPORAL
            ? [...activeCohortBuilderItem.temporalConstraints, newFilter]
            : activeCohortBuilderItem.temporalConstraints,
      };
      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          conceptGroups: state.conceptGroups,
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType:
            action.payload === FilterTypeLegacy.TEMPORAL
              ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /** When a user modifies a filter's input field value */
    setActiveCohortBuilderItemFilterInputFieldValue(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        filterId: string;
        filterType: FilterTypeLegacy;
        inputFieldId: string;
        value: string;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }
      const filterType = action.payload.filterType;
      const filterId = action.payload.filterId;
      const inputFieldId = action.payload.inputFieldId;
      const value = action.payload.value;

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        conceptFilters:
          filterType === FilterTypeLegacy.CONCEPT
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.conceptFilters,
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.conceptFilters,
        numericFilters:
          filterType === FilterTypeLegacy.NUMERIC
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.numericFilters,
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.numericFilters,
        relatedEntityFilters:
          filterType === FilterTypeLegacy.RELATED_ENTITY
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.relatedEntityFilters ?? [],
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.relatedEntityFilters,
        temporalConstraints:
          filterType === FilterTypeLegacy.TEMPORAL
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.temporalConstraints,
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.temporalConstraints,
        uniquenessFilters:
          filterType === FilterTypeLegacy.UNIQUENESS
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.uniquenessFilters ?? [],
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.uniquenessFilters,
        countFilter:
          filterType === FilterTypeLegacy.COUNT
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.countFilter ? [activeCohortBuilderItem.countFilter] : [],
                filterId,
                inputFieldId,
                value
              )[0]
            : activeCohortBuilderItem.countFilter,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          conceptGroups: state.conceptGroups,
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType:
            action.payload.filterType === FilterTypeLegacy.TEMPORAL
              ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /** When a user deletes a filter */
    deleteActiveCohortBuilderItemFilter(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        filterId: string;
        filterType: FilterTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        conceptFilters:
          action.payload.filterType === FilterTypeLegacy.CONCEPT
            ? activeCohortBuilderItem.conceptFilters.filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.conceptFilters,
        numericFilters:
          action.payload.filterType === FilterTypeLegacy.NUMERIC
            ? activeCohortBuilderItem.numericFilters.filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.numericFilters,
        relatedEntityFilters:
          action.payload.filterType === FilterTypeLegacy.RELATED_ENTITY
            ? (activeCohortBuilderItem.relatedEntityFilters ?? []).filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.relatedEntityFilters,
        temporalConstraints:
          action.payload.filterType === FilterTypeLegacy.TEMPORAL
            ? activeCohortBuilderItem.temporalConstraints.filter(
                (temporalConstraint) => temporalConstraint.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.temporalConstraints,
        uniquenessFilters:
          action.payload.filterType === FilterTypeLegacy.UNIQUENESS
            ? (activeCohortBuilderItem.uniquenessFilters ?? []).filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.uniquenessFilters,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType:
            action.payload.filterType === FilterTypeLegacy.TEMPORAL
              ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },
    // setLoggedInUserIsCohortOwner(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
    //   return {
    //     ...state,
    //     isLoggedInUserCohortOwner: action.payload,
    //   };
    // },
    setCohortUpdatedByBuilder(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCohortUpdatedByBuilder: action.payload,
      };
    },
  },

  /**
   * The extra reducers below perform automatic state updates when certain actions are dispatched.
   */
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        action.type === cohortBuilderSlice.actions['setInitialCohortBuilderSliceState'].type,
      (state) => {
        const { cohortBuilderItemNamesToIdsMap, cohortBuilderItemIdsToNamesMap } =
          getCohortBuilderItemNameAndIdMaps(state.cohortDefinition);
        return {
          ...state,
          cohortBuilderWorkflow: {
            // activeStep: DEFAULT_COHORT_BUILDER_STEP,
            hasUnsavedChanged: false,
            isProcessEnabled: state.cohortDefinition
              ? validateCohortDefinitionForProcessing(state.cohortDefinition)
              : false,
            modal: undefined,
            // actionModal: state.cohortBuilderWorkflow?.actionModal,
            inUseCohortBuilderItemsMap: getInUseCohortBuilderItemsMap(
              state.cohortDefinition,
              cohortBuilderItemNamesToIdsMap
            ),
            cohortBuilderItemNamesToIdsMap,
            cohortBuilderItemIdsToNamesMap,
          },
        };
      }
    );
    /** Clear the active cohort builder item state when the item is saved or canceled */
    builder.addMatcher(
      (action) => {
        const actionsToMatch = [
          cohortBuilderSlice.actions['updateCohortBuilderSliceState'].type,
          cohortBuilderSlice.actions['setIndexDate'].type,
          cohortBuilderSlice.actions['deleteIndexDate'].type,
          cohortBuilderSlice.actions['setSubjectVariable'].type,
          cohortBuilderSlice.actions['deleteSubjectVariable'].type,
          cohortBuilderSlice.actions['setSimpleEvent'].type,
          cohortBuilderSlice.actions['deleteSimpleEvent'].type,
          cohortBuilderSlice.actions['setDerivedEvent'].type,
          cohortBuilderSlice.actions['deleteDerivedEvent'].type,
          cohortBuilderSlice.actions['setStudyPeriodRestrictions'].type,
          cohortBuilderSlice.actions['setCriteriaSelection'].type,
        ];
        return actionsToMatch.includes(action.type);
      },
      (state) => {
        if (state.cohortBuilderWorkflow === undefined) {
          throw new Error('cohortBuilder extraReducer: cohortBuilderWorkflow is undefined');
        }

        if (state.cohortDefinition === undefined) {
          throw new Error('cohortBuilder extraReducer: cohortDefinition is undefined');
        }

        const { cohortBuilderItemNamesToIdsMap, cohortBuilderItemIdsToNamesMap } =
          getCohortBuilderItemNameAndIdMaps(state.cohortDefinition);
        return {
          ...state,
          cohortBuilderWorkflow: {
            // activeStep: state.cohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,
            hasUnsavedChanged: validateCohortDefinitionForSave(state.cohortDefinition),
            isProcessEnabled: validateCohortDefinitionForProcessing(state.cohortDefinition),
            modal: undefined,
            // actionModal: undefined,
            inUseCohortBuilderItemsMap: getInUseCohortBuilderItemsMap(
              state.cohortDefinition,
              cohortBuilderItemNamesToIdsMap
            ),
            cohortBuilderItemNamesToIdsMap,
            cohortBuilderItemIdsToNamesMap,
            viewedCohortBuilderItems: state.cohortBuilderWorkflow?.viewedCohortBuilderItems ?? {},
          },
          activeCohortBuilderItem: undefined,
        };
      }
    );
  },
});

export const {
  /** Set cohort definition */
  resetCohortBuilderSlice,
  setInitialCohortBuilderSliceState,
  updateCohortBuilderSliceState,

  /** Concept group action */
  setConceptGroups,

  /** Index Date Actions */
  setIndexDate,
  deleteIndexDate,

  /** Subject Variable Actions */
  setSubjectVariable,
  deleteSubjectVariable,

  /** Simple Event Actions */
  setSimpleEvent,
  deleteSimpleEvent,

  /** Derived Event Actions */
  setDerivedEvent,
  deleteDerivedEvent,

  /** Study Period Restriction Actions */
  setStudyPeriodRestrictions,

  /** Cohort Definition Selection Actions */
  setCriteriaSelection,

  /** Workflow Actions */
  // setActiveStep,
  setModal,
  clearModal,
  // setActionModal,
  // clearActionModal,
  markItemViewed,

  /** Active Cohort Builder Item Actions */
  // setActiveIndexDateItem,
  // setActiveSubjectsVariableItem,
  setActiveCohortBuilderItemName,
  setActiveCohortBuilderItemSubjectVariableType,
  setActiveCohortBuilderItemSimpleEventType,
  setActiveCohortBuilderItemDerivedEventType,
  setActiveCohortBuilderItemIndexDateType,
  setActiveCohortBuilderItemIndexDateValue,
  setActiveCohortBuilderItemAnyOrder,
  addActiveCohortBuilderItemFilter,
  setActiveCohortBuilderItemFilterInputFieldValue,
  deleteActiveCohortBuilderItemFilter,
  addActiveCohortBuilderEventForDerivedEventType,
  setActiveCohortBuilderEventInputFieldValue,
  deleteActiveCohortBuilderEvent,
  // setLoggedInUserIsCohortOwner,
  setCohortUpdatedByBuilder,
} = cohortBuilderSlice.actions;

declare module '@verantos-dev/app-state-base' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof cohortBuilderSlice> {}
}

// Inject the slice into the root reducer
cohortBuilderSlice.injectInto(rootReducer);
